import { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header";
import UserReportModal from "../../core/modals/userReportModal"; // Assuming this is your modal name
import { fetchAllContacts } from "../../api/contact";
import { ApexOptions } from "apexcharts";
import Select from "react-select";
import PageLoadProgressBar from "../../core/data/json/pageLoadProgressBar";
import Chart from "react-apexcharts";
import KpiCard from "../../core/common/KPIcard";
import { useDebounce } from "../../api/usedebounce";
import Tip from "../../core/common/tooltip";
import ChartExportControl from "../../core/common/ChartExportControl";
import { components } from "react-select";
import ScrollToTop from "../../core/common/ScrollToTop";

const TimeFrameValueContainer = (props: any) => {
  return (
    <components.ValueContainer {...props}>
      <i
        className="ti ti-calendar-stats me-2 text-muted"
        style={{ fontSize: "16px" }}
      />
      {props.children}
    </components.ValueContainer>
  );
};

const UserReport = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState("Monthly");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);
  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("add_calls");
    const cleanup2 = makeModalDraggable("edit_calls");
    const cleanup3 = makeModalDraggable("delete_calls");
    const cleanup4 = makeModalDraggable("bulk_delete_modal");
    const cleanup5 = makeModalDraggable("import_excel_modal");
    const cleanup6 = makeModalDraggable("user_report_modal");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
      cleanup6?.();
    };
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchAllContacts();
        setCompanies(data || []);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (companies.length > 0) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    }
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    if (!term) return companies;

    return companies.filter(
      ({ company }) =>
        company.company_name?.toLowerCase().includes(term) ||
        company.leadsource_name?.toLowerCase().includes(term) ||
        company.industry?.toLowerCase().includes(term)
    );
  }, [debouncedSearch, companies]);

  // ================= By Source (Donut) =================
  const getBySource = useMemo(() => {
    const sourceMap: Record<string, number> = {};
    filteredCompanies.forEach(({ company }) => {
      const source = company.leadsource_name || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });
    const labels = Object.keys(sourceMap);
    const series = Object.values(sourceMap);
    return { labels, series };
  }, [filteredCompanies]);

  const totalSource = useMemo(
    () => getBySource.series.reduce((a, b) => a + b, 0),
    [getBySource.series]
  );

  const donutOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "donut", animations: { enabled: true, speed: 600 } },
      labels: getBySource.labels,
      colors: [
        "#0092E4",
        "#4A00E5",
        "#00B894",
        "#FFA201",
        "#E41F07",
        "#5A5AFE",
      ],
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 270,
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: { show: true, fontSize: "14px" },
              value: {
                show: true,
                fontSize: "22px",
                fontWeight: 600,
                formatter: (val) => `${val}`,
              },
              total: {
                show: true,
                label: "Total Companies",
                fontSize: "13px",
                formatter: () => `${totalSource}`,
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (value: number) => {
            const percent = ((value / totalSource) * 100).toFixed(1);
            return `${value} companies (${percent}%)`;
          },
        },
      },
      legend: {
        position: "bottom",
        fontSize: "13px",
        formatter: (val, opts) => {
          const count = getBySource.series[opts.seriesIndex];
          const percent = ((count / totalSource) * 100).toFixed(1);
          return `${val} — ${count} (${percent}%)`;
        },
      },
      responsive: [{ breakpoint: 768, options: { chart: { height: 280 } } }],
    }),
    [getBySource, totalSource]
  );

  // ================= By TimeFrame (Bar) =================
  const getByMonth = useMemo(() => {
    const monthMap: Record<string, number> = {};
    const formatter: Intl.DateTimeFormatOptions = {
      month: "short",
      year: "numeric",
    };

    filteredCompanies.forEach(({ company }) => {
      const created = new Date(company.created_at);
      let key = created.toLocaleString("default", formatter);

      if (timeFrame === "Quarterly") {
        const q = Math.floor(created.getMonth() / 3) + 1;
        key = `Q${q} ${created.getFullYear()}`;
      } else if (timeFrame === "Yearly") {
        key = created.getFullYear().toString();
      }

      monthMap[key] = (monthMap[key] || 0) + 1;
    });

    const sortedKeys = Object.keys(monthMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
    return sortedKeys.map((key) => ({ x: key, y: monthMap[key] }));
  }, [filteredCompanies, timeFrame]);

  const barOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 275,
        toolbar: { show: false },
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: { bar: { distributed: true, borderRadius: 6 } },
      colors: ["#00918E"],
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 4, borderColor: "#e9ecef" },
      xaxis: {
        type: "category",
        title: {
          text: timeFrame,
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      yaxis: {
        title: {
          text: "Number of Companies",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      tooltip: { y: { formatter: (val) => `${val} companies` } },
      responsive: [{ breakpoint: 768, options: { chart: { height: 240 } } }],
    }),
    [getByMonth, timeFrame]
  );

  // ================= Industry Funnel (Replacement for Status) =================
  const getIndustryFunnel = useMemo(() => {
    const industryMap: Record<string, number> = {};
    filteredCompanies.forEach(({ company }) => {
      const industry = company.industry || "Unknown";
      industryMap[industry] = (industryMap[industry] || 0) + 1;
    });

    return Object.entries(industryMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6) // Top 6 industries
      .map(([name, count]) => ({ x: name, y: count }));
  }, [filteredCompanies]);

  const funnelOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        height: 350,
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 8,
          barHeight: "70%",
          distributed: true,
        },
      },
      colors: [
        "#00918E",
        "#00B894",
        "#FFA201",
        "#E41F07",
        "#4A00E5",
        "#0092E4",
      ],
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val} companies`,
        style: { fontSize: "14px", fontWeight: 600, colors: ["#fff"] },
      },
      xaxis: { type: "category", labels: { show: false } },
      yaxis: {
        title: {
          text: "Top Industries",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      grid: { show: false },
      tooltip: { y: { formatter: (val: number) => `${val} companies` } },
      title: {
        text: "Companies by Industry",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      legend: { show: false },
      responsive: [{ breakpoint: 768, options: { chart: { height: 300 } } }],
    }),
    []
  );

  // ================= Heatmap (Activity by Day of Week) =================
  const getHeatmapData = useMemo(() => {
    const monthMap: Record<string, number[]> = {};

    filteredCompanies.forEach(({ company }) => {
      const date = new Date(company.created_at);
      const monthKey = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      const dayIndex = date.getDay();

      if (!monthMap[monthKey]) monthMap[monthKey] = [0, 0, 0, 0, 0, 0, 0];
      monthMap[monthKey][dayIndex]++;
    });

    const sortedMonths = Object.keys(monthMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return sortedMonths.map((month) => ({
      name: month,
      data: monthMap[month],
    }));
  }, [filteredCompanies]);

  const heatmapOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "heatmap",
        height: 450,
        toolbar: { show: false },
        animations: { enabled: true },
      },
      dataLabels: { enabled: false },
      colors: ["#008FFB"],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.8,
          radius: 8,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: "#f8f9fa" },
              { from: 1, to: 5, color: "#E0F7FA" },
              { from: 6, to: 15, color: "#80DEEA" },
              { from: 16, to: 30, color: "#00ACC1" },
              { from: 31, to: 999, color: "#006064" },
            ],
          },
        },
      },
      xaxis: {
        categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        title: {
          text: "Day of Week",
          style: { fontSize: "12px", fontWeight: 500 },
        },
      },
      yaxis: {
        title: { text: "Month", style: { fontSize: "12px", fontWeight: 500 } },
      },
      title: {
        text: "Company Creation Activity Heatmap",
        align: "center",
        style: { fontSize: "15px", fontWeight: 600 },
      },
      tooltip: {
        y: {
          formatter: (val: number) =>
            val === 0 ? "No activity" : `${val} companies`,
        },
      },
      legend: { position: "bottom" },
    }),
    []
  );

  // ================= KPI Calculations =================
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const totalCompanies = filteredCompanies.length;

  const companiesThisMonth = useMemo(
    () =>
      filteredCompanies.filter(
        ({ company }) => new Date(company.created_at) >= firstDayOfMonth
      ).length,
    [filteredCompanies]
  );

  const topSource = useMemo(() => {
    if (filteredCompanies.length === 0) return { name: "-", count: 0 };
    const map: Record<string, number> = {};
    filteredCompanies.forEach(({ company }) => {
      const source = company.leadsource_name || "Unknown";
      map[source] = (map[source] || 0) + 1;
    });
    const [name, count] = Object.entries(map).sort((a, b) => b[1] - a[1])[0];
    return { name, count };
  }, [filteredCompanies]);

  const avgPerMonth = useMemo(() => {
    if (filteredCompanies.length === 0) return 0;
    const dates = filteredCompanies.map(
      ({ company }) => new Date(company.created_at)
    );
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const monthDiff =
      (now.getFullYear() - minDate.getFullYear()) * 12 +
      (now.getMonth() - minDate.getMonth()) +
      1;
    return Math.round(totalCompanies / monthDiff);
  }, [filteredCompanies, totalCompanies]);

  const timeFrameSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 40,
      borderRadius: 8,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 1px #0d6efd" : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      cursor: "pointer",
    }),
    option: (base: any, state: any) => ({
      ...base,
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
          ? "#f1f5ff"
          : "#fff",
      color: state.isSelected ? "#fff" : "#212529",
      cursor: "pointer",
    }),
    singleValue: (base: any) => ({
      ...base,
      fontWeight: 500,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { color: "#0d6efd" },
    }),
    valueContainer: (base: any) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      paddingLeft: 12,
    }),
  };


  const totalCompaniesPercent = "100";

  const companiesThisMonthPercent =
    totalCompanies === 0
      ? "0"
      : ((companiesThisMonth / totalCompanies) * 100).toFixed(1);

  const topSourcePercent =
    totalCompanies === 0
      ? "0"
      : ((topSource.count / totalCompanies) * 100).toFixed(1);

  const avgPerMonthPercent =
    totalCompanies === 0
      ? "0"
      : ((avgPerMonth / totalCompanies) * 100).toFixed(1);

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <h4 className="page-title d-flex align-items-center gap-2">
                  <i className="ti ti-file-invoice text-primary" />
                  User Report
                  <Tip
                    text="Overview of company/user registration trends, sources, industries, and activity patterns."
                    placement="right"
                  >
                    <i
                      className="fa fa-info-circle text-primary"
                      style={{ cursor: "pointer", fontSize: "15px" }}
                    />
                  </Tip>
                </h4>
                <small className="text-muted">
                  Total: {filteredCompanies.length} Companies
                </small>
              </div>
              <div className="col-sm-4 text-sm-end">
                <div className="head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>


         <div className="row g-3 mb-4">
  {(() => {
    const total = totalCompanies;

    const thisMonthPercent =
      total > 0 ? ((companiesThisMonth / total) * 100).toFixed(1) : "0";

    const avgPerMonthPercent =
      total > 0 ? ((avgPerMonth / total) * 100).toFixed(1) : "0";

    const topSourcePercent =
      total > 0 ? ((topSource.count / total) * 100).toFixed(1) : "0";

    return (
      <>
        <div className="row g-3">
          {/* TOTAL COMPANIES */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Total Companies: ${totalCompanies} (100%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Companies"
                  subtitle="Total"
                  value={totalCompanies}
                  icon="ti ti-building"
                  color="primary"
                  tooltip="Total number of companies in the system"
                />
              </div>
            </Tip>
          </div>

          {/* NEW THIS MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`New This Month: ${companiesThisMonth} (${thisMonthPercent}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Companies"
                  subtitle="This Month"
                  value={companiesThisMonth}
                  icon="ti ti-trending-up"
                  color="success"
                  tooltip="Companies registered in the current month"
                  percentage={thisMonthPercent}
                  percentageColor={
                    parseFloat(thisMonthPercent) > 0 ? "success" : "danger"
                  }
                  percentageIcon={
                    parseFloat(thisMonthPercent) > 0
                      ? "ti ti-arrow-up"
                      : "ti ti-arrow-down"
                  }
                />
              </div>
            </Tip>
          </div>

          {/* TOP SOURCE */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Top Lead Source: ${topSource.name} (${topSourcePercent}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Lead Source"
                  subtitle={`Top: ${topSource.name}`}
                  value={topSource.count}
                  icon="ti ti-broadcast"
                  color="warning"
                  tooltip="Lead source generating the most companies"
                  percentage={topSourcePercent}
                  percentageColor="success"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>

          {/* AVG PER MONTH */}
          <div className="col-12 col-sm-6 col-lg-3">
            <Tip
              text={`Avg / Month: ${avgPerMonth} (${avgPerMonthPercent}%)`}
              placement="top"
            >
              <div className="kpi-card-wrapper h-100">
                <KpiCard
                  title="Companies"
                  subtitle="Avg / Month"
                  value={avgPerMonth}
                  icon="ti ti-chart-line"
                  color="info"
                  tooltip="Average companies registered per month"
                  percentage={avgPerMonthPercent}
                  percentageColor="info"
                  percentageIcon="ti ti-arrow-up"
                />
              </div>
            </Tip>
          </div>
        </div>
      </>
    );
  })()}
</div>



          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="input-group w-50">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by company, source, industry…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    className="btn btn-light border"
                    onClick={() => setSearchInput("")}
                    title="Clear"
                  >
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>

              <Select
                className="w-25"
                options={[
                  { value: "", label: "Choose" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Quarterly", label: "Quarterly" },
                  { value: "Yearly", label: "Yearly" },
                ]}
                defaultValue={{ value: "Monthly", label: "Monthly" }}
                isSearchable={false}
                styles={timeFrameSelectStyles}
                components={{
                  ValueContainer: TimeFrameValueContainer,
                }}
                onChange={(e) => setTimeFrame(e?.value || "Monthly")}
              />

              <button
  type="button"
  className="btn btn-primary"
  data-bs-toggle="modal"
  data-bs-target="#user_report_modal"
  onClick={() =>("user_report_modal")}
  style={{
                                position: "relative",
                                overflow: "hidden",
                              }}
>
  <i className="ti ti-file-download me-2" /> Download Report
  <span
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                                  transform: "skewX(-20deg) translateX(-150%)",
                                  animation: "shine 3s infinite",
                                }}
                              />
</button>

            </div>

            {debouncedSearch && filteredCompanies.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-database-off fs-2 mb-2" />
                <p>No companies found for "{debouncedSearch}"</p>
              </div>
            )}

            <div className="card-body">
              <div className="row g-4">
                {/* By TimeFrame */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="companies-by-timeframe">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-bar text-primary" />
                            Companies by {timeFrame}
                            <Tip text="Number of companies registered in each period.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            Showing {getByMonth.length} periods
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getByMonth}
                            filename={`Companies_by_${timeFrame}`}
                            cardId="companies-by-timeframe"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getByMonth.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-bar-off fs-2 mb-2" />
                          <p>No data available</p>
                        </div>
                      ) : (
                        <Suspense fallback={<div>Loading chart...</div>}>
                          <Chart
                            options={barOptions}
                            series={[{ name: "Companies", data: getByMonth }]}
                            type="bar"
                            height={275}
                          />
                        </Suspense>
                      )}
                    </div>
                  </div>
                </div>

                {/* By Source */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="companies-by-source">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-nowrap">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-chart-donut text-primary" />
                            Companies by Source
                            <Tip text="Distribution of companies by lead source.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getBySource.labels.length} sources
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getBySource.series.map((val, i) => ({
                              Source: getBySource.labels[i],
                              Count: val,
                            }))}
                            filename="Companies_by_Source"
                            cardId="companies-by-source"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center">
                      {getBySource.series.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-pie-off fs-2 mb-2" />
                          <p>No source data</p>
                        </div>
                      ) : (
                        <div style={{ width: "100%", height: "350px" }}>
                          <Suspense fallback={<div>Loading chart...</div>}>
                            <Chart
                              options={donutOptions}
                              series={getBySource.series}
                              type="donut"
                              height={350}
                              width="100%"
                            />
                          </Suspense>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* By Industry (Funnel Style) */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="top-industries">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-building-skyscraper text-primary" />
                            Top Industries
                            <Tip text="Most common industries among registered companies.">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">Top 6 industries</small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getIndustryFunnel}
                            filename="Top_Industries"
                            cardId="top-industries"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getIndustryFunnel.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-filter-off fs-2 mb-2" />
                          <p>No industry data</p>
                        </div>
                      ) : (
                        <Suspense fallback={<div>Loading chart...</div>}>
                          <Chart
                            options={funnelOptions}
                            series={[{ data: getIndustryFunnel }]}
                            type="bar"
                            height={350}
                          />
                        </Suspense>
                      )}
                    </div>
                  </div>
                </div>

                {/* Heatmap */}
                <div className="col-lg-6">
                  <div className="card h-100 shadow-sm" id="registration-activity">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <i className="ti ti-calendar-stats text-primary" />
                            Registration Activity
                            <Tip text="Heatmap showing when companies typically register (day of week).">
                              <i
                                className="fa fa-info-circle text-primary"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                              />
                            </Tip>
                          </h5>
                          <small className="text-muted">
                            {getHeatmapData.length} months
                          </small>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                          <ChartExportControl
                            data={getHeatmapData.map((m) => ({
                              Month: m.name,
                              Sun: m.data[0],
                              Mon: m.data[1],
                              Tue: m.data[2],
                              Wed: m.data[3],
                              Thu: m.data[4],
                              Fri: m.data[5],
                              Sat: m.data[6],
                            }))}
                            filename="Registration_Activity_Heatmap"
                            cardId="registration-activity"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {getHeatmapData.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-calendar-off fs-2 mb-2" />
                          <p>No activity data</p>
                        </div>
                      ) : (
                        <Suspense fallback={<div>Loading chart...</div>}>
                          <Chart
                            options={heatmapOptions}
                            series={getHeatmapData}
                            type="heatmap"
                            height={450}
                          />
                        </Suspense>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <UserReportModal />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default UserReport;
